import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useLogin, useVerify2FA } from '@/hooks/useAuth'
import { LoginRequest } from '@/types'
import { Eye, EyeOff, Mail, Lock, LogIn, Shield, Clock, Copy, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { GoogleLoginButton } from '@/components/GoogleLoginButton'

const Login = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [requires2FA, setRequires2FA] = useState(false)
  const [twoFactorEmail, setTwoFactorEmail] = useState('')
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const [timeRemaining, setTimeRemaining] = useState(300) // 5 minutos em segundos
  const [codeCopied, setCodeCopied] = useState(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const loginMutation = useLogin()
  const verify2FAMutation = useVerify2FA()

  const { register, handleSubmit, formState: { errors } } = useForm<LoginRequest>()

  const onSubmit = async (data: LoginRequest) => {
    try {
      const response = await loginMutation.mutateAsync(data)
      console.log('Login response:', response)
      
      // Se 2FA está habilitado (TRUE), o backend retorna LoginTwoFactorResponse
      // com email e message - Neste caso, mostrar campo para código de 6 dígitos
      if (('requiresTwoFactor' in response && response.requiresTwoFactor === true) ||
          ('email' in response && 'message' in response && !('token' in response))) {
        // Usar o email da resposta se disponível, senão usar o email do formulário
        setTwoFactorEmail(response.email || data.email)
        setTimeRemaining(300) // Resetar timer para 5 minutos
        setRequires2FA(true)
        return
      }
      
      // Se 2FA está desabilitado (FALSE), o backend retorna LoginResponse direto
      // com token e usuario - Neste caso, fazer login direto sem campo 2FA
      if (response && 'token' in response && response.token && 'usuario' in response && response.usuario) {
        console.log('Login successful (2FA disabled), redirecting...')
        // Redirecionar para a página de origem ou home
        const from = location.state?.from?.pathname || '/'
        navigate(from, { replace: true })
      } else {
        console.log('Unexpected response structure:', response)
      }
    } catch (error) {
      console.error('Login error:', error)
      // Erro já tratado no hook
    }
  }

  const onSubmit2FA = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      return
    }

    try {
      const response = await verify2FAMutation.mutateAsync({
        email: twoFactorEmail,
        code: twoFactorCode
      })

      if (response && response.token && response.usuario) {
        // Limpar timer antes de redirecionar
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
        // Redirecionar para a página de origem ou home
        const from = location.state?.from?.pathname || '/'
        navigate(from, { replace: true })
      }
    } catch (error: any) {
      console.error('2FA verification error:', error)
      // Tratar erros específicos de 2FA
      if (error?.response?.data?.erro || error?.message) {
        const errorMessage = error.response?.data?.erro || error.message || 'Erro ao verificar código'
        if (errorMessage.toLowerCase().includes('expirado')) {
          // Código expirado - resetar fluxo
          handleBackToLogin()
        } else if (errorMessage.toLowerCase().includes('incorreto') || errorMessage.toLowerCase().includes('inválido')) {
          // Código incorreto - apenas mostrar erro (já tratado no hook)
        }
      }
      // Erro já tratado no hook
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setTwoFactorCode(value)
  }

  const handleBackToLogin = () => {
    // Limpar timer se existir
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    setRequires2FA(false)
    setTwoFactorCode('')
    setTwoFactorEmail('')
    setTimeRemaining(300) // Resetar timer
  }

  const handleResendCode = async () => {
    // Reenviar código fazendo novo login
    // Precisamos do email e senha, mas não temos mais acesso a eles aqui
    // A melhor abordagem é voltar para o formulário de login
    handleBackToLogin()
  }

  const handleCopyCode = async () => {
    if (twoFactorCode && twoFactorCode.length === 6) {
      try {
        await navigator.clipboard.writeText(twoFactorCode)
        setCodeCopied(true)
        toast.success('Código copiado!', {
          duration: 2000,
          icon: '📋'
        })
        setTimeout(() => setCodeCopied(false), 2000)
      } catch (err) {
        console.error('Erro ao copiar código:', err)
        // Fallback para navegadores mais antigos
        const textArea = document.createElement('textarea')
        textArea.value = twoFactorCode
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          setCodeCopied(true)
          toast.success('Código copiado!', {
            duration: 2000,
            icon: '📋'
          })
          setTimeout(() => setCodeCopied(false), 2000)
        } catch (err) {
          console.error('Erro ao copiar código (fallback):', err)
          toast.error('Erro ao copiar código', {
            duration: 6000
          })
        }
        document.body.removeChild(textArea)
      }
    } else {
      toast.error('Digite o código completo antes de copiar', {
        duration: 6000
      })
    }
  }

  // Função para colar código automaticamente quando o usuário colar algo
  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    // Remover caracteres não numéricos e limitar a 6 dígitos
    const cleanedCode = pastedText.replace(/\D/g, '').slice(0, 6)
    setTwoFactorCode(cleanedCode)
    
    // Se o código tiver 6 dígitos, verificar automaticamente após um pequeno delay
    if (cleanedCode.length === 6) {
      // Pequeno delay para garantir que o estado foi atualizado
      setTimeout(async () => {
        try {
          const response = await verify2FAMutation.mutateAsync({
            email: twoFactorEmail,
            code: cleanedCode
          })

          if (response && response.token && response.usuario) {
            // Limpar timer antes de redirecionar
            if (timerRef.current) {
              clearInterval(timerRef.current)
              timerRef.current = null
            }
            // Redirecionar para a página de origem ou home
            const from = location.state?.from?.pathname || '/'
            navigate(from, { replace: true })
          }
        } catch (error: any) {
          console.error('2FA verification error:', error)
          // Tratar erros específicos de 2FA
          if (error?.response?.data?.erro || error?.message) {
            const errorMessage = error.response?.data?.erro || error.message || 'Erro ao verificar código'
            if (errorMessage.toLowerCase().includes('expirado')) {
              // Código expirado - resetar fluxo
              handleBackToLogin()
            }
          }
          // Erro já tratado no hook
        }
      }, 100)
    }
  }

  // Contador regressivo de 5 minutos para o código 2FA
  useEffect(() => {
    if (!requires2FA) {
      // Limpar timer quando não está em modo 2FA
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }

    // Limpar timer anterior se existir
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }

    // Criar novo timer
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1
        if (newTime <= 0) {
          if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
          }
          return 0
        }
        return newTime
      })
    }, 1000) // Atualiza a cada segundo

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [requires2FA])

  // Função para formatar o tempo restante (MM:SS)
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Mostrar formulário de 2FA se necessário
  if (requires2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
              <Shield className="h-6 w-6 text-primary-600" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Verificação em Duas Etapas
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Um código de verificação foi enviado para:
            </p>
            <p className="mt-1 text-center text-sm font-medium text-gray-900">
              <strong>{twoFactorEmail}</strong>
            </p>
            <p className="mt-2 text-center text-xs text-gray-500">
              Digite o código de 6 dígitos recebido por e-mail
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={(e) => { e.preventDefault(); onSubmit2FA(); }}>
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                Código de Verificação
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="code"
                  value={twoFactorCode}
                  onChange={handleCodeChange}
                  onPaste={handlePaste}
                  maxLength={6}
                  className="input text-center text-2xl font-mono tracking-widest pr-12"
                  placeholder="000000"
                  autoFocus
                  disabled={timeRemaining === 0}
                />
                {twoFactorCode.length === 6 && (
                  <button
                    type="button"
                    onClick={handleCopyCode}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 text-gray-500 hover:text-primary-600 transition-colors"
                    title={codeCopied ? 'Código copiado!' : 'Copiar código'}
                  >
                    {codeCopied ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500 text-center">
                💡 Dica: Você pode colar o código diretamente do e-mail
              </p>
              <div className="mt-2 flex items-center justify-center space-x-2">
                <Clock className={`h-4 w-4 ${
                  timeRemaining > 120 ? 'text-green-600' : 
                  timeRemaining > 60 ? 'text-yellow-600' : 
                  timeRemaining > 0 ? 'text-orange-600' : 
                  'text-red-600'
                }`} />
                <p className={`text-xs ${
                  timeRemaining > 120 ? 'text-green-600' : 
                  timeRemaining > 60 ? 'text-yellow-600' : 
                  timeRemaining > 0 ? 'text-orange-600' : 
                  'text-red-600'
                }`}>
                  {timeRemaining > 0 ? (
                    <>Tempo restante: <strong>{formatTime(timeRemaining)}</strong></>
                  ) : (
                    <strong>Código expirado. Faça login novamente.</strong>
                  )}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="flex-1 btn btn-outline"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  disabled={verify2FAMutation.isLoading || twoFactorCode.length !== 6 || timeRemaining === 0}
                  className="flex-1 btn btn-primary disabled:opacity-50"
                >
                  {verify2FAMutation.isLoading ? 'Verificando...' : timeRemaining === 0 ? 'Código Expirado' : 'Verificar'}
                </button>
              </div>
              <button
                type="button"
                onClick={handleResendCode}
                className="w-full text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Reenviar Código
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <LogIn className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Entre na sua conta
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Ou{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              crie uma nova conta
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                E-mail
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'E-mail é obrigatório',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'E-mail inválido'
                    }
                  })}
                  type="email"
                  className="input pl-10"
                  placeholder="Digite seu e-mail"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="senha" className="block text-sm font-medium text-gray-700">
                Senha
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('senha', { required: 'Senha é obrigatória' })}
                  type={showPassword ? 'text' : 'password'}
                  className="input pl-10 pr-10"
                  placeholder="Digite sua senha"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.senha && (
                <p className="mt-1 text-sm text-red-600">{errors.senha.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Esqueceu sua senha?
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loginMutation.isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loginMutation.isLoading ? 'Entrando...' : 'Entrar'}
            </button>
          </div>

          {/* Separador */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 text-gray-500">Ou</span>
            </div>
          </div>

          {/* Botão Google Login */}
          <div>
            <GoogleLoginButton />
          </div>
        </form>
      </div>
    </div>
  )
}

export default Login
